import { Box } from "@mui/material";
import type { ReactNode } from "react";
import { macro } from "../ui/macroTokens";
import { v2, nums } from "../ui/v2Tokens";
import { qualityProteinPercent, type MacroSet, type MacroTotals } from "../model/nutritionV2Model";
import { GRAM, KCAL, num } from "../ui/format";
import { energyLabel, macroText, POINTS, POINTS_SHORT } from "../ui/nutritionUnits";
import { useNutritionDisplayMode } from "../../../../contexts/NutritionDisplayContext";
import { inDisplayUnit, pointsNum, remainingLabel, targetStatus, type TargetMetric } from "./targetsMath";

const f1 = (n: number) => Math.round(n * 10) / 10;

type CellProps = {
  label: string;
  /** which figure this is — what it converts to in points mode (fibre never does) */
  metric: TargetMetric;
  current: number;
  target: number;
  /** the number's colour and the swatch beside the label */
  color: string;
  unit?: string;
  sub?: ReactNode;
  /**
   * A second reading of the same macro, beside the label: how many grams of it per kilo of the
   * trainee. It belongs to the heading rather than under the number because it qualifies WHICH
   * figure this is, not how the count is going.
   */
  perKg?: string;
  testId?: string;
  /** the log reports what was eaten; the builder counts down to a finished menu */
  isLog?: boolean;
  /** calories lead the grid: wider, larger, and washed in the brand */
  lead?: boolean;
};

/**
 * One target, as a distance rather than a pair of numbers: how far along, and exactly how
 * much is still missing. Building a menu is a countdown to the target, so "נותרו 640" is
 * the number the trainer is actually working with.
 */
const MacroCell = ({ label, metric, current, target, color, unit = ` ${GRAM}`, sub, perKg, testId, isLog, lead }: CellProps) => {
  const mode = useNutritionDisplayMode();
  const inPoints = mode === POINTS && metric !== "fiber";
  // shared with the mini strip the phone shows in place of this grid — see targetsMath. Judged on
  // the figures this cell prints, so in points mode the colour and the sentence agree with them.
  const { over, pct } = targetStatus(inDisplayUnit(current, metric, mode), inDisplayUnit(target, metric, mode));
  const remaining = remainingLabel(current, target, { unit, isLog, mode, metric });
  // in points mode every figure but fibre is points — energy and macro alike, one decimal
  const figure = (n: number) =>
    inPoints ? pointsNum(inDisplayUnit(n, metric, mode)) : unit === "" ? num(n) : String(f1(n));

  return (
    <Box
      data-testid={testId}
      sx={{
        // Tight on purpose. The builder PINS this bar, so its height is subtracted from every
        // screenful of menu underneath it for as long as the trainer is on the page — it was
        // spending a fifth of the viewport to say five numbers.
        padding: "9px 13px 11px",
        // cells are divided by a hairline rather than being separate cards — five cards in a
        // row read as five things, one grid reads as one budget
        borderInlineStart: { xs: "none", md: `1px solid ${v2.lineSoft}` },
        borderTop: { xs: `1px solid ${v2.lineSoft}`, md: "none" },
        ...(lead
          ? {
              borderInlineStart: "none",
              borderTop: "none",
              background: "rgba(1,187,167,.035)",
              gridColumn: { xs: "1 / -1", md: "auto" },
            }
          : {}),
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          fontSize: "11.5px",
          fontWeight: 500,
          color: v2.ink2,
          marginBottom: "4px",
        }}
      >
        <Box sx={{ width: "6px", height: "6px", borderRadius: "2px", background: color, flex: "none" }} />
        {label}
        {perKg && (
          <Box component="span" sx={{ color: v2.ink3, whiteSpace: "nowrap", ...nums }}>
            {perKg}
          </Box>
        )}
      </Box>

      <Box sx={{ display: "flex", alignItems: "baseline", gap: "4px", marginBottom: "6px", ...nums }}>
        <Box
          sx={{
            fontSize: lead ? "23px" : "19px",
            fontWeight: 600,
            lineHeight: 1,
            letterSpacing: "-.025em",
            color: over ? v2.over : v2.ink,
          }}
        >
          {figure(current)}
        </Box>
        <Box sx={{ fontSize: "12px", fontWeight: 500, color: over ? v2.over : v2.ink2 }}>
          {inPoints ? POINTS_SHORT : unit === "" ? KCAL : GRAM}
        </Box>
        <Box sx={{ fontSize: "12px", color: v2.ink4 }}>
          / {figure(target)}
        </Box>
      </Box>

      <Box sx={{ height: "4px", borderRadius: "99px", background: "#EDF1F4", overflow: "hidden" }}>
        <Box
          sx={{
            height: "100%",
            borderRadius: "99px",
            width: `${Math.min(100, Math.max(0, pct))}%`,
            background: over ? v2.over : color,
            transition: "width .35s cubic-bezier(.4,0,.2,1)",
          }}
        />
      </Box>

      {target > 0 && (
        <Box
          data-testid={testId ? `${testId}-remaining` : undefined}
          sx={{ marginTop: "6px", fontSize: "11.5px", color: over ? v2.over : v2.ink3, ...nums }}
        >
          {remaining}
        </Box>
      )}
      {sub && (
        <Box
          sx={{
            marginTop: "3px",
            fontSize: "11px",
            color: v2.ink4,
            display: "flex",
            alignItems: "center",
            gap: "5px",
            ...nums,
          }}
        >
          <Box sx={{ width: "5px", height: "5px", borderRadius: "50%", background: v2.brand, flex: "none" }} />
          {sub}
        </Box>
      )}
    </Box>
  );
};

type BarProps = {
  totals: MacroTotals;
  targets: MacroSet;
  /**
   * "builder" counts DOWN to a menu being finished; "log" reports what a trainee actually
   * ate against the plan. Same cells, different sentence.
   */
  context?: "builder" | "log";
  /** the card's own heading — the builder titles it, the log lets the page do that */
  title?: string;
  /** the targets as one line, folded into the head so the page needs no second card */
  goalSummary?: ReactNode;
  /** the head's end — "ערוך מחשבון" on the builder */
  action?: ReactNode;
  /**
   * The trainee's weight, in kg, so the protein cell can report grams per kilo — the unit a coach
   * actually prescribes protein in, and the one the calculator's own slider is set in. Omitted
   * where nobody has said (a template belongs to no trainee, the food log is not prescribing), and
   * the line is then simply absent rather than divided by a guess.
   */
  bodyWeightKg?: number;
};

/**
 * The bar no longer positions itself.
 *
 * It used to carry `position: sticky; top: 0; zIndex: 20`, which is what CLIPPED it: the builder
 * page's own header is `sticky; top: 0; zIndex: 30`, so the bar stuck to the same offset, lost the
 * stacking contest, and slid underneath — the numbers row sliced away, the progress bars and
 * "נותרו X" left showing below the chrome. `top: 0` was ignoring the height of the header above
 * it. Whoever OWNS the layout knows that height; this component does not, so the pages do the
 * sticking now (see MenuBuilderV2Page / MenuTemplateBuilderPage). The food log, which deliberately
 * has no sticky ancestor and must not cover the day tabs, simply gets a bar that never sticks —
 * the same behaviour its `sticky={false}` used to buy, now the only behaviour there is.
 */
const TargetsSummaryBar = ({
  totals,
  targets,
  context = "builder",
  title,
  goalSummary,
  action,
  bodyWeightKg = 0,
}: BarProps) => {
  const mode = useNutritionDisplayMode();
  /*
   * Grams per kilo of the trainee — the unit the three macros are actually prescribed in, and the
   * one the calculator's own sliders are set in, so it is what a coach checks a menu against
   * without dividing in their head.
   *
   * Undefined where nobody has said what the trainee weighs (a template belongs to no trainee, the
   * food log is not prescribing), and the reading is then simply absent rather than divided by a
   * number this component invented.
   */
  const perKg = (grams: number): string | undefined =>
    bodyWeightKg > 0 ? `${(grams / bodyWeightKg).toFixed(2)} ${GRAM}/ק״ג` : undefined;

  return (
    <Box
      data-testid="targets-summary-bar"
      sx={{
        borderRadius: v2.rLg,
        border: `1px solid ${v2.line}`,
        background: v2.surface,
        boxShadow: v2.shadow,
        overflow: "hidden",
      }}
    >
      {/*
        No overall "N% of the calorie target" line here. Every cell below already carries its
        own bar and its own "נותרו X", and the calories cell says the same thing in the same
        words — a summary of the summary just pushed the grid further down the page.
      */}
      {(title || goalSummary || action) && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            flexWrap: "wrap",
            padding: "7px 13px",
            borderBottom: `1px solid ${v2.lineSoft}`,
          }}
        >
          {title && <Box sx={{ fontSize: "13px", fontWeight: 600, color: v2.ink }}>{title}</Box>}
          {goalSummary && <Box sx={{ fontSize: "12.5px", color: v2.ink3, ...nums }}>{goalSummary}</Box>}
          {action && <Box sx={{ marginInlineStart: "auto" }}>{action}</Box>}
        </Box>
      )}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr 1fr", md: "1.35fr 1fr 1fr 1fr 1fr" },
        }}
      >
        <MacroCell
          lead
          isLog={context === "log"}
          testId="summary-calories"
          label={energyLabel(mode)}
          metric="calories"
          current={totals.calories}
          target={targets.calories}
          color={v2.brand}
          unit=""
        />
        <MacroCell
          isLog={context === "log"}
          testId="summary-protein"
          label="חלבון"
          metric="protein"
          current={totals.protein}
          target={targets.protein}
          color={macro.protein.text}
          perKg={perKg(totals.protein)}
          // quality protein has no target of its own — it is a property of the protein already
          // counted, so it reads as a share of it rather than as its own cell
          sub={
            totals.protein > 0
              ? `מתוכו איכותי: ${macroText("protein", totals.qualityProtein, mode)} · ${qualityProteinPercent(totals)}%`
              : undefined
          }
        />
        <MacroCell
          isLog={context === "log"}
          testId="summary-carbs"
          label="פחמימות"
          metric="carbs"
          current={totals.carbs}
          target={targets.carbs}
          color={macro.carbs.text}
          perKg={perKg(totals.carbs)}
        />
        <MacroCell
          isLog={context === "log"}
          testId="summary-fats"
          label="שומנים"
          metric="fats"
          current={totals.fats}
          target={targets.fats}
          color={macro.fats.text}
          perKg={perKg(totals.fats)}
        />
        <MacroCell
          isLog={context === "log"}
          testId="summary-fiber"
          label="סיבים"
          metric="fiber"
          current={totals.fiber}
          target={targets.fiber}
          color={macro.fiber.text}
        />
      </Box>
    </Box>
  );
};

export default TargetsSummaryBar;
